import React from 'react';
import {
  Globe, ShoppingCart, Mail, Phone, MessageCircle, Video, Music,
  FileText, Image, Book, Briefcase, Heart, Star, Zap, Coffee,
  Camera, Headphones, Tv, Gamepad2, Code, Palette, Layers,
  Facebook, Twitter, Instagram, Linkedin, Github, Youtube,
  Twitch, Send, Music2, Radio, Calendar,
  MapPin, DollarSign, Gift, Tag, Ticket, Users, TrendingUp,
  FileVideo, Podcast, Newspaper, GraduationCap, Building2
} from 'lucide-react';

// Custom Discord logo component
const DiscordIcon = ({ className = 'w-5 h-5' }) => (
  <svg 
    viewBox="0 0 24 24" 
    className={className}
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.956-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419c0-1.333.955-2.419 2.157-2.419c1.21 0 2.176 1.096 2.157 2.42c0 1.333-.946 2.418-2.157 2.418z"/>
  </svg>
);

// Icon mapping for link types
export const linkIcons = {
  'globe': { icon: Globe, label: 'Website' },
  'shopping-cart': { icon: ShoppingCart, label: 'Shop' },
  'mail': { icon: Mail, label: 'Email' },
  'phone': { icon: Phone, label: 'Phone' },
  'message-circle': { icon: MessageCircle, label: 'Chat' },
  'video': { icon: Video, label: 'Video' },
  'music': { icon: Music, label: 'Music' },
  'file-text': { icon: FileText, label: 'Document' },
  'image': { icon: Image, label: 'Gallery' },
  'book': { icon: Book, label: 'Blog' },
  'briefcase': { icon: Briefcase, label: 'Portfolio' },
  'heart': { icon: Heart, label: 'Favorite' },
  'star': { icon: Star, label: 'Featured' },
  'zap': { icon: Zap, label: 'Premium' },
  'coffee': { icon: Coffee, label: 'Support' },
  'camera': { icon: Camera, label: 'Photography' },
  'headphones': { icon: Headphones, label: 'Podcast' },
  'tv': { icon: Tv, label: 'Streaming' },
  'gamepad': { icon: Gamepad2, label: 'Gaming' },
  'code': { icon: Code, label: 'Code' },
  'palette': { icon: Palette, label: 'Design' },
  'layers': { icon: Layers, label: 'Projects' },
  'calendar': { icon: Calendar, label: 'Events' },
  'map-pin': { icon: MapPin, label: 'Location' },
  'dollar-sign': { icon: DollarSign, label: 'Payment' },
  'gift': { icon: Gift, label: 'Gift' },
  'tag': { icon: Tag, label: 'Store' },
  'ticket': { icon: Ticket, label: 'Tickets' },
  'users': { icon: Users, label: 'Community' },
  'trending-up': { icon: TrendingUp, label: 'Analytics' },
  'file-video': { icon: FileVideo, label: 'Course' },
  'podcast': { icon: Podcast, label: 'Audio' },
  'newspaper': { icon: Newspaper, label: 'News' },
  'graduation-cap': { icon: GraduationCap, label: 'Education' },
  'building': { icon: Building2, label: 'Business' }
};

// Social platform icons with brand recognition
export const socialPlatforms = {
  'twitter': { icon: Twitter, label: 'Twitter/X', color: '#1DA1F2', placeholder: 'https://twitter.com/username' },
  'facebook': { icon: Facebook, label: 'Facebook', color: '#1877F2', placeholder: 'https://facebook.com/username' },
  'instagram': { icon: Instagram, label: 'Instagram', color: '#E4405F', placeholder: 'https://instagram.com/username' },
  'linkedin': { icon: Linkedin, label: 'LinkedIn', color: '#0A66C2', placeholder: 'https://linkedin.com/in/username' },
  'github': { icon: Github, label: 'GitHub', color: '#181717', placeholder: 'https://github.com/username' },
  'youtube': { icon: Youtube, label: 'YouTube', color: '#FF0000', placeholder: 'https://youtube.com/@username' },
  'twitch': { icon: Twitch, label: 'Twitch', color: '#9146FF', placeholder: 'https://twitch.tv/username' },
  'telegram': { icon: Send, label: 'Telegram', color: '#26A5E4', placeholder: 'https://t.me/username' },
  'discord': { icon: DiscordIcon, label: 'Discord', color: '#5865F2', placeholder: 'discord.gg/server or username#0000' },
  'tiktok': { icon: Music2, label: 'TikTok', color: '#000000', placeholder: 'https://tiktok.com/@username' },
  'spotify': { icon: Radio, label: 'Spotify', color: '#1DB954', placeholder: 'https://open.spotify.com/user/username' },
  'snapchat': { icon: Camera, label: 'Snapchat', color: '#FFFC00', placeholder: 'https://snapchat.com/add/username' },
  'pinterest': { icon: Image, label: 'Pinterest', color: '#E60023', placeholder: 'https://pinterest.com/username' },
  'reddit': { icon: MessageCircle, label: 'Reddit', color: '#FF4500', placeholder: 'https://reddit.com/u/username' },
  'whatsapp': { icon: Phone, label: 'WhatsApp', color: '#25D366', placeholder: '+1234567890' },
  'email': { icon: Mail, label: 'Email', color: '#EA4335', placeholder: 'your@email.com' },
  'website': { icon: Globe, label: 'Website', color: '#06b6d4', placeholder: 'https://yourwebsite.com' }
};

export const getLinkIcon = (iconName, className = 'w-5 h-5') => {
  const iconData = linkIcons[iconName];
  if (!iconData) return <Globe className={className} />;
  const IconComponent = iconData.icon;
  return <IconComponent className={className} />;
};

export const getSocialIcon = (platform, className = 'w-5 h-5') => {
  const socialData = socialPlatforms[platform];
  if (!socialData) return null;
  const IconComponent = socialData.icon;
  return <IconComponent className={className} />;
};

export const IconPicker = ({ value, onChange, type = 'link' }) => {
  const icons = type === 'link' ? linkIcons : socialPlatforms;
  
  return (
    <div className="icon-picker mt-2">
      <div className="grid grid-cols-6 gap-2 max-h-48 overflow-y-auto p-2 bg-slate-900/50 rounded-xl border border-white/10">
        {Object.entries(icons).map(([key, data]) => {
          const IconComponent = data.icon;
          return (
            <button
              key={key}
              type="button"
              className={`flex items-center justify-center p-3 rounded-lg transition-all ${
                value === key 
                  ? 'bg-cyan-500/20 border-2 border-cyan-500 text-cyan-400' 
                  : 'bg-slate-800/50 border-2 border-transparent text-slate-400 hover:bg-cyan-950/30 hover:text-cyan-400'
              }`}
              onClick={() => onChange(key)}
              title={data.label}
              data-testid={`icon-${key}`}
            >
              <IconComponent className="w-5 h-5" />
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default {
  linkIcons,
  socialPlatforms,
  getLinkIcon,
  getSocialIcon,
  IconPicker
};
